// Agent queue (#6210) — which pull requests and tasks one roster agent holds right now,
// so a maintainer can tell a PR an agent is queued for, running or watching from one
// that sits outside every lane. Pure: which tasks an agent owns, the live-status
// buckets, the row's state, and the keyset cursor the page mints. The rows are read in
// `@developerz/db` (`queries/agent-queue.ts`); `GET /v1/agents/queue`
// (`apps/api/src/routes/agents.ts`) is the one door.
//
// WHO OWNS A TASK is the roster's origin map, INVERTED and filtered to the CLAIMABLE
// origins ({@link agentClaimableOrigins}) — never a second list here — so an origin the
// roster hands to an agent joins that agent's queue, once some build can claim it, with no
// edit in this file. The one hand-stated arm is escalation: it owns no origin, and its work is the
// stuck-PR rescue rounds (#6172), `pr_babysit` rows whose spec carries `rescue`. Those
// rows stay in the CI fixer's queue too: a rescue round IS a `pr_babysit` round, and
// the CI fixer's queue lists every live one; the escalation queue is the subset the
// escalation model was brought in for.
//
// The escalation queue also lists STUCK PRs (#6338 wave 2, replacing the narrower upcoming
// hand-over projection of #6236): open AI-managed pull requests idle past the read's floor,
// each parked on what is holding it. They are not tasks the agent holds, so they are not
// derived from a task status here; `./stuck-pr.ts` classifies them (`stuckPrsOf`), and the
// escalation queue's first page leads with them, longest idle first.

import { type TaskHoldReason, TaskStatus } from '../entities/task';
import { AGENT_ROSTER, AgentId, type AgentRoster } from './agent-roster';
import { TaskIdSchema } from './ids';
import { decodeListCursor, encodeListCursor, ListCollection, type SortTerm } from './list-query';
import type { StuckPrRow } from './stuck-pr';
import { originClaimableToday, type TaskOrigin } from './task-envelope';

/** Default page size of `GET /v1/agents/queue`. */
export const AGENT_QUEUE_DEFAULT_PER_PAGE = 25;
/** Largest page `GET /v1/agents/queue` serves. */
export const AGENT_QUEUE_MAX_PER_PAGE = 100;

/** Where a live task sits, as the maintainer reads it. */
export const AgentQueueState = {
  /** `pending`: waiting for a box, or held (`hold_reason`). */
  Queued: 'queued',
  /** On a box now (`runner_id`, `run_id`). */
  Running: 'running',
  /** Its PR is open and the babysit watch is following it to a merge. */
  Watching: 'watching',
  /**
   * A parked AI-managed PR idle past the stuck read's floor (#6338) — not a task the agent
   * holds. Escalation's queue only; last, so the drawer lists the rounds in flight before
   * the PRs still stuck behind them.
   */
  Stuck: 'stuck',
} as const;
export type AgentQueueState = (typeof AgentQueueState)[keyof typeof AgentQueueState];
/** Every {@link AgentQueueState}, in the order the drawer groups them. */
export const AGENT_QUEUE_STATES: readonly AgentQueueState[] = Object.values(AgentQueueState);

/** The states a TASK row carries — every queue state but the escalation queue's `stuck` arm. */
export type AgentQueueTaskState = Exclude<AgentQueueState, (typeof AgentQueueState)['Stuck']>;
/** Every {@link AgentQueueTaskState} — the wire's task arm is a closed enum over these. */
export const AGENT_QUEUE_TASK_STATES: readonly AgentQueueTaskState[] = AGENT_QUEUE_STATES.filter(
  (s): s is AgentQueueTaskState => s !== AgentQueueState.Stuck,
);

/**
 * The state a task status sits in, or `null` for a status no queue holds. A total
 * `switch` with no default over {@link TaskStatus}: a status added to the machine is a
 * compile error here until someone decides whether a queue shows it. A task status never
 * lands in the `stuck` state — that arm is a PR the escalation queue lists beside tasks.
 *
 * `completed` is `null`: for a `pr` task it is the hop before the watch, and for a
 * babysit round it is the round's end. Neither is work an agent still holds.
 */
export function agentQueueState(status: TaskStatus): AgentQueueTaskState | null {
  switch (status) {
    case TaskStatus.Pending:
      return AgentQueueState.Queued;
    case TaskStatus.Assigned:
    case TaskStatus.InProgress:
    // #6747: shown in the running group, labelled "Paused" (`AgentQueueList`'s `STATUS_LABEL`).
    case TaskStatus.Paused:
      return AgentQueueState.Running;
    case TaskStatus.AwaitingCi:
    case TaskStatus.AwaitingReview:
    case TaskStatus.Mergeable:
      return AgentQueueState.Watching;
    case TaskStatus.Completed:
    case TaskStatus.Merged:
    case TaskStatus.Failed:
    case TaskStatus.Cancelled:
      return null;
  }
}

/** The live statuses, DERIVED from {@link agentQueueState} — the list the read filters on. */
export const AGENT_QUEUE_STATUSES: readonly TaskStatus[] = Object.values(TaskStatus).filter(
  (s) => agentQueueState(s) !== null,
);

/** Which tasks one agent's queue holds, or that it holds none. */
export type AgentQueueScope =
  /** Live tasks of these origins. */
  | { readonly kind: 'origins'; readonly origins: readonly TaskOrigin[] }
  /** Live `pr_babysit` rows whose spec carries `rescue` (#6172). */
  | { readonly kind: 'rescue_rounds' }
  /** The agent runs no task of its own: no claimable roster origin maps to it and it is not escalation ({@link agentQueueScope}). */
  | { readonly kind: 'none' };

/** A scope that names tasks — what the read takes once the door has refused `none`. */
export type AgentQueueReadScope = Exclude<AgentQueueScope, { kind: 'none' }>;

/**
 * The origins bound to `agent` that some build can claim today (`originClaimableToday`), in
 * roster order. THE one place an agent's claimability is computed: the queue
 * ({@link agentQueueScope}) and the dashboard's "Not in use" both read it, so an origin
 * bound before any build can claim it (`plan_author` until #6765) opens no queue and
 * leaves its agent Not in use, and the two flip together when its token is derived.
 */
export function agentClaimableOrigins(
  agent: AgentId,
  roster: AgentRoster = AGENT_ROSTER,
): TaskOrigin[] {
  return (Object.entries(roster.origin) as [TaskOrigin, AgentRoster['origin'][TaskOrigin]][])
    .filter(([origin, owner]) => owner === agent && originClaimableToday(origin))
    .map(([origin]) => origin);
}

/**
 * The tasks `agent` holds: the claimable origins the roster gives it
 * ({@link agentClaimableOrigins}), the rescue rounds for escalation, else none. `roster`
 * is a seam for the test that a newly owned origin joins the queue.
 */
export function agentQueueScope(
  agent: AgentId,
  roster: AgentRoster = AGENT_ROSTER,
): AgentQueueScope {
  const origins = agentClaimableOrigins(agent, roster);
  if (origins.length > 0) return { kind: 'origins', origins };
  if (agent === AgentId.Escalation) return { kind: 'rescue_rounds' };
  return { kind: 'none' };
}

/** One live task as the queue read answers it. */
export type AgentQueueRow = {
  readonly id: string;
  readonly status: TaskStatus;
  readonly origin: TaskOrigin;
  readonly repoFullName: string;
  /** The PR the task is about (`taskPrNumberSql`), or null for a task with none yet. */
  readonly prNumber: number | null;
  readonly title: string;
  readonly holdReason: TaskHoldReason | null;
  readonly holdUntil: Date | null;
  readonly runnerId: string | null;
  readonly runId: string | null;
  readonly createdAt: Date;
  readonly updatedAt: Date;
};

/** A row with the state the drawer groups it under — a task the agent holds. */
export type AgentQueueTaskItem = AgentQueueRow & { readonly state: AgentQueueTaskState };

/**
 * A stuck PR as the escalation queue lists it (#6338): a {@link StuckPrRow} — a pull
 * request, not a task — stamped with the `stuck` state so the wire's one union serves both
 * arms. Built by {@link agentQueueStuckItem}; the row itself is `stuckPrsOf`'s, never
 * re-derived here.
 */
export type AgentQueueStuckItem = StuckPrRow & {
  readonly state: (typeof AgentQueueState)['Stuck'];
};

/** One row of the escalation queue's first page: a task the agent holds, or a stuck PR. */
export type AgentQueueItem = AgentQueueTaskItem | AgentQueueStuckItem;

/**
 * Stamp {@link AgentQueueItem.state} on a task the agent holds. THROWS on a status no
 * queue holds: the read filters on {@link AGENT_QUEUE_STATUSES}, so one here is a read
 * that drifted from this file, and serving it under a guessed state would hide that.
 */
export function agentQueueItem(row: AgentQueueRow): AgentQueueTaskItem {
  const state = agentQueueState(row.status);
  if (state === null) {
    throw new Error(
      `agent queue: task ${row.id} has status ${row.status}, which no queue holds — ` +
        'the read filters on AGENT_QUEUE_STATUSES, so the read and this file disagree',
    );
  }
  return { ...row, state };
}

/** Stamp the `stuck` state on a classified stuck PR. Pure. */
export function agentQueueStuckItem(row: StuckPrRow): AgentQueueStuckItem {
  return { ...row, state: AgentQueueState.Stuck };
}

/** The queue's one order: newest update first, the task id breaking a tie. */
const QUEUE_ORDER: readonly SortTerm[] = [{ field: 'updated_at', dir: 'desc' }];

/** A queue page's position: the last served row's `(updated_at, id)`. */
export type AgentQueuePosition = { readonly updatedAt: Date; readonly id: string };

/**
 * The opaque `next_cursor` after `last` — the shared list codec, stamped with its own
 * collection, so a `/v1/tasks` token (also ordered on tasks) is refused here.
 */
export function encodeAgentQueueCursor(last: AgentQueuePosition): string {
  return encodeListCursor(ListCollection.AgentQueue, QUEUE_ORDER, [last.updatedAt, last.id]);
}

/**
 * The position a token names, or `null` for anything this read did not mint: another
 * collection, another order, a key that is not a timestamp and a task id. The caller
 * answers `null` with its own 400; never a restart from the head.
 */
export function decodeAgentQueueCursor(token: string): AgentQueuePosition | null {
  const position = decodeListCursor(token);
  if (position === null || position.collection !== ListCollection.AgentQueue) return null;
  const [term] = position.sort;
  if (position.sort.length !== 1 || term?.field !== 'updated_at' || term.dir !== 'desc') {
    return null;
  }
  const [updatedAt, id] = position.keys;
  if (!(updatedAt instanceof Date) || !TaskIdSchema.safeParse(id).success) return null;
  return { updatedAt, id: id as string };
}
